import { useLazyQuery } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'

import {
  buildFeatureSet,
  getCredentials,
  hasRequiredFeatures
} from '../../cache/auth'
import PageHeader from '../../components/PageHeader'
import { ADD_ON_POLICIES_QUERY } from '../../graphql/addOnPolicies'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import cancelPolicyPagesRoleManagement, {
  ADD_ON_POLICIES_ROUTE
} from '../../staticRoleManagement/cancelPolicyRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { AddOnPolicy } from '../../types/cancelAndModifyPolicies'
import PoliciesFilters from '../CancelPoliciesList/PoliciesFilters'
import useStyles from '../CancelPoliciesList/styles/index.styles'
import { AddOnPoliciesTable } from './AddOnPoliciesTable'

const PER_PAGE = 5

type SectionName =
  | 'ADD_POLICY_BUTTON'
  | 'EDIT_POLICY'
  | 'DELETE_POLICY'
  | 'PROVIDER_SELECTOR'

// A section is usable when the role may write it and the caller holds its feature; the
// feature half keeps a button from leading to a route the router would refuse.
const canUseSection = (section: SectionName): boolean => {
  const config = cancelPolicyPagesRoleManagement.find(
    (item) => item.route === ADD_ON_POLICIES_ROUTE
  )?.sections?.[section]
  if (!config?.rolesListWithWriteAccess?.includes(getRoleFromStorage())) {
    return false
  }
  return hasRequiredFeatures(
    config.requiredFeatures,
    buildFeatureSet(getCredentials())
  )
}

const AddOnPoliciesList: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [page, setPage] = useState(1)
  const [autoCompleteProvider, setAutoCompleteProvider] = useState<any>()
  const [searchInputValue, setSearchInputValue] = useState<string>('')

  const [getPolicies, { data, loading, refetch }] = useLazyQuery<{
    addOnPolicies: AddOnPolicy[]
  }>(ADD_ON_POLICIES_QUERY, { notifyOnNetworkStatusChange: true })

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER
  // A provider sees its own sets; everyone else picks a provider first.
  const providerCode: string | undefined = isRoleProvider
    ? getEntityCode()
    : autoCompleteProvider?.code

  useEffect(() => {
    if (providerCode) getPolicies({ variables: { providerCode } })
  }, [providerCode])

  const filteredPolicies = useMemo(
    () =>
      providerCode
        ? matchSorter(data?.addOnPolicies ?? [], searchInputValue, {
            keys: ['basicDetails.name', 'basicDetails.description']
          })
        : [],
    [data, searchInputValue, providerCode]
  )

  useEffect(() => setPage(1), [searchInputValue, providerCode])

  const numberOfPages = Math.ceil(filteredPolicies.length / PER_PAGE)

  // A delete can empty the last page; fall back to the new last page instead of a blank table.
  useEffect(() => {
    if (page > Math.max(numberOfPages, 1)) setPage(Math.max(numberOfPages, 1))
  }, [page, numberOfPages])
  const canCreate = canUseSection('ADD_POLICY_BUTTON')

  return (
    <div>
      <PageHeader title={getTranslate('Add-On Policies')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Add-On Policies')}
        </Typography>
        {canCreate && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to="/policies/add-on-policies/create">
            {getTranslate(isSmallScreen ? 'Add' : 'Add Add-On Policy')}
          </Button>
        )}
      </div>
      <PoliciesFilters
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        hasAccessToChangeProvider={canUseSection('PROVIDER_SELECTOR')}
        autoCompleteProvider={autoCompleteProvider}
        setAutoCompleteProvider={setAutoCompleteProvider}
        loadingForCancelPolicy={loading}
        isRoleProvider={isRoleProvider}
      />
      {providerCode ? (
        <>
          <AddOnPoliciesTable
            data={filteredPolicies.slice(
              (page - 1) * PER_PAGE,
              page * PER_PAGE
            )}
            loading={loading}
            providerCode={providerCode}
            canEdit={canUseSection('EDIT_POLICY')}
            canDelete={canUseSection('DELETE_POLICY')}
            refetch={() => refetch?.()}
          />
          {!loading && filteredPolicies.length === 0 && (
            <div className={classes.noTenantSelected}>
              <Typography variant="h2">
                {getTranslate('This provider has no add-on policies yet')}
              </Typography>
            </div>
          )}
          <div className={classes.paginationContainer}>
            <Pagination
              count={numberOfPages}
              color="primary"
              size={isSmallScreen ? 'small' : 'medium'}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </div>
        </>
      ) : (
        <div className={classes.noTenantSelected}>
          <Typography variant="h2">
            {getTranslate('Select a provider to see its policies')}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default AddOnPoliciesList
